import { useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, RotateCw } from "lucide-react";
import { TopBar, ActionButton, SecondaryAction } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import LoadFailureNote from "./LoadFailureNote";
import PlanGoalManage from "./PlanGoalManage";
import { normalizeGoalStatus } from "../lib/goalStatus";
import { GOAL_CATEGORY_LABELS } from "../lib/goalCategory";
import api from "../lib/api";
import {
  STATE_LABELS,
  FIRST_UP,
  NEXT_UP,
  WAITS,
  STARTING,
  SAVING,
  SAVE_FAILED,
  TRY_AGAIN,
  ALL_DONE,
  ALL_DONE_NOTE,
  liveIndex,
  isRowInteractive,
} from "../lib/planMilestonesState";

// D166 M9 / brief-764.md:35 — the drawing's own casing ("Short term", "In
// progress") differs from the app's shared vocabulary (`lib/goalStatus.js`'s
// Title Case "In Progress", `DevelopmentPlan.jsx`'s "Short-term"). The
// flag-on leg prints the DRAWN casing; the flag-off leg (today's list page)
// is completely unchanged and keeps reading from the shared modules as
// before. Keyed on the NORMALIZED status (issue #764 review round 1, F3) —
// `normalizeGoalStatus` (`lib/goalStatus.js`) maps the pre-#193 legacy
// `"active"` value to `"in_progress"` for display; a raw lookup on
// `goal.status` missed that and printed the fallback "Not started" for a
// legacy-active goal opened on this step.
const STATUS_LABELS_DRAWN = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};
const HORIZON_LABELS_DRAWN = { short: "Short term", long: "Long term" };

/** A blank field reads "Not set" in the drawn muted colour — never a blank,
 * a dash or a nought (brief-764.md ruling 3). Shared by Due, Category,
 * Horizon and Notes — issue #764 review round 1, F5: a blank category or
 * horizon must read "Not set" too, never a fallback label ("General",
 * "Short term") standing in for data the goal does not actually carry. */
function FactValue({ value, testId }) {
  if (value) {
    return (
      <span data-testid={testId} style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45, color: "var(--nw-ink)" }}>
        {value}
      </span>
    );
  }
  return (
    <span data-testid={testId} style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-ink-faint)" }}>
      Not set
    </span>
  );
}

function FactRow({ label, children }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontSize: 13, color: "var(--nw-ink-muted)" }}>{label}</span>
        {children}
      </div>
      <div style={{ height: 1, background: "var(--nw-line-faint)" }} />
    </>
  );
}

/** B3/#760's own skeleton primitive, repeated here — see that file's header
 * for why a plain rounded bar with no independent testid is the right
 * shape (nothing here is meant to be asserted on individually). */
function SkeletonBar({ width }) {
  return <div aria-hidden="true" style={{ height: 14, width, borderRadius: 6, background: "var(--nw-line)" }} />;
}

/** The ring/half-filled/tick glyph at the left of a row, mirroring the
 * selected word exactly (artboard commentary, `PlanMilestonesState.dc.html`
 * state "face"): a plain ring for `not_started`, a half-filled circle for
 * `going`, a filled tick for `done`. */
function MilestoneGlyph({ state, live }) {
  if (state === "done") {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx="11" cy="11" r="10" fill="var(--nw-navy)" />
        <path d="M6.5 11.2l3 3 6-6.2" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (state === "going") {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx="11" cy="11" r="10" stroke="var(--nw-navy)" strokeWidth="2" />
        <path d="M11 3a8 8 0 0 1 0 16z" fill="var(--nw-navy)" />
      </svg>
    );
  }
  return (
    <div
      aria-hidden="true"
      style={{
        width: 22,
        height: 22,
        border: `2px solid ${live ? "var(--nw-navy)" : "var(--nw-line-strong)"}`,
        borderRadius: "var(--nw-radius-pill)",
        flexShrink: 0,
        marginTop: 1,
        boxSizing: "border-box",
      }}
    />
  );
}

const MILESTONE_STATE_ORDER = ["not_started", "going", "done"];

/**
 * The three-word radio group (issue #1021 step 3). `disabled` covers BOTH
 * reasons a row can't be tapped right now — it's not the interactive row
 * (`lib/planMilestonesState.js`'s `isRowInteractive` — waiting its turn,
 * D193.2, or a `done` row that isn't the one undo-eligible row, round 2
 * review F2), or ANY save anywhere on the page is in flight (the
 * artboard's "saving" state: "one save at a time... every segment on the
 * page is inert", round 2 review F1). Only the exact segment a save is
 * running for (`savingSegment`) gets the in-flight treatment; every other
 * segment, on every row, falls back to the same muted styling a
 * genuinely waiting row already uses.
 *
 * Round 2 review F6 — the WAI-ARIA "radio group" pattern
 * (https://www.w3.org/WAI/ARIA/apg/patterns/radio/), not a row of three
 * independent buttons: ONE tab stop per group (roving `tabindex`, always
 * the CHECKED segment — the standard starting point, since focus tracks
 * selection here), Left/Right and Up/Down move AND select (wrapping),
 * Space re-affirms the focused segment (a no-op most of the time, since
 * arrow-driven selection already moved it), and Enter does nothing — a
 * radio is not a button. `aria-labelledby` names the group from the row's
 * own milestone text (`titleId`), never a generic "radio group".
 */
function MilestoneSegments({ index, state, disabled, savingSegment, onTap, titleId }) {
  const itemRefs = useRef({});

  function selectAndFocus(nextState) {
    onTap(nextState);
    // `states` (hence `state`/`checked`) only updates once the tap's own
    // PUT resolves and the parent re-renders — focus has to wait for that
    // same tick (the segment that will carry `tabindex="0"` next render)
    // rather than moving immediately, or it lands on a segment that is
    // about to lose its only tab stop.
    requestAnimationFrame(() => itemRefs.current[nextState]?.focus());
  }

  // Round 3 review N1 — lives on each `role="radio"` segment, not the
  // group: `eslint-plugin-jsx-a11y`'s `interactive-supports-focus` (the
  // GROUP carrying a handler with no way to take focus itself) and
  // `click-events-have-key-events` (a segment carrying `onClick` with no
  // keyboard equivalent OF ITS OWN) both cleared this way, verified —
  // this repo's lint config (`eslint.config.mjs`) ratchets the jsx-a11y
  // count down permanently, never up, so the fix has to be structural,
  // not a suppression. Reading `state` from the closure (not from which
  // segment physically has focus) means the exact same function works
  // attached to any of the three spans — the WAI-ARIA behaviour (arrows
  // move+select from the CHECKED value, Space re-affirms, Enter does
  // nothing) is unchanged; only which element owns the listener moved.
  function handleKeyDown(e) {
    if (disabled) return;
    const order = MILESTONE_STATE_ORDER;
    const from = order.indexOf(state);
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      selectAndFocus(order[(from + 1) % order.length]);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      selectAndFocus(order[(from - 1 + order.length) % order.length]);
    } else if (e.key === " ") {
      e.preventDefault();
      onTap(state);
    }
    // Enter: deliberately unhandled — WAI-ARIA radios do not activate on
    // Enter (round 2 review F6).
  }

  // The WAI-ARIA radio group pattern keeps the GROUP itself out of tab
  // order on purpose — roving tabindex lives on the individual
  // `role="radio"` segments below, one of which is always tabbable, and
  // each of those now owns its own `onKeyDown` too (round 3 review N1).
  return (
    <div
      data-testid={`milestone-state-${index}`}
      role="radiogroup"
      aria-disabled={disabled ? "true" : undefined}
      aria-labelledby={titleId}
      style={{
        display: "flex",
        marginTop: 6,
        border: `1px solid ${disabled ? "var(--nw-line)" : "var(--nw-line-strong)"}`,
        borderRadius: "var(--nw-radius-control)",
        overflow: "hidden",
      }}
    >
      {MILESTONE_STATE_ORDER.map((segState, i) => {
        const checked = state === segState;
        const isSaving = savingSegment === segState;
        const muted = disabled && !isSaving;
        const background = isSaving
          ? "var(--nw-navy)"
          : muted
          ? checked
            ? "var(--nw-line)"
            : "var(--nw-page)"
          : checked
          ? "var(--nw-navy)"
          : "var(--nw-surface)";
        const color = isSaving
          ? "#FFFFFF"
          : muted
          ? checked
            ? "var(--nw-ink-lede)"
            : "var(--nw-ink-faint)"
          : checked
          ? "#FFFFFF"
          : "var(--nw-navy)";
        return (
          <span
            key={segState}
            ref={(el) => {
              itemRefs.current[segState] = el;
            }}
            data-testid={`milestone-state-${index}-${segState.replace("_", "-")}`}
            role="radio"
            aria-checked={checked}
            tabIndex={disabled ? -1 : checked ? 0 : -1}
            onClick={disabled ? undefined : () => onTap(segState)}
            onKeyDown={disabled ? undefined : handleKeyDown}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              minHeight: 44,
              padding: "0 4px",
              background,
              color,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              borderLeft: i > 0 ? `1px solid ${disabled ? "var(--nw-line)" : "var(--nw-line-strong)"}` : "none",
              cursor: disabled ? "default" : "pointer",
            }}
          >
            {isSaving && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {isSaving ? SAVING : STATE_LABELS[segState]}
          </span>
        );
      })}
    </div>
  );
}

/**
 * The drawn "save-failed" state (artboard `data-dc-state="save-failed"`):
 * an alert-left-rule message (D55/D57 — stays on the page, never a toast,
 * D109) plus Try again, which repeats the exact tap that failed. Shared by
 * a row's own failure AND the gold's (round 2 review F3 — no drawn state
 * names the gold specifically, but D57's "failures stay on the page" rule
 * makes no exception for it either): `state` is whatever is ACTUALLY
 * saved right now (the control shows what is saved, not what was
 * tapped — SAVE_FAILED's own contract), `"not_started"` for the gold
 * (a failed "Start the first one" leaves milestone 0 exactly there).
 */
function SaveErrorBlock({ state, onRetry }) {
  return (
    <div className="flex flex-col" style={{ gap: 4, marginTop: 6 }}>
      <div
        data-testid="milestones-save-error"
        className="flex items-start"
        style={{
          gap: 8,
          padding: "10px 12px",
          background: "var(--nw-line-faint)",
          borderLeft: "3px solid var(--nw-alert)",
          borderRadius: "var(--nw-radius-control)",
          fontSize: 13,
          lineHeight: 1.5,
          color: "var(--nw-ink)",
        }}
      >
        <AlertCircle size={16} color="var(--nw-alert)" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
        <span>{SAVE_FAILED(state)}</span>
      </div>
      <button
        type="button"
        data-testid="milestones-save-retry"
        onClick={onRetry}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          minHeight: 40,
          padding: "0 14px",
          border: "1px solid var(--nw-line-strong)",
          borderRadius: "var(--nw-radius-control)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--nw-navy)",
          background: "var(--nw-surface)",
        }}
      >
        <RotateCw size={14} aria-hidden="true" />
        {TRY_AGAIN}
      </button>
    </div>
  );
}

/**
 * Issue #764 — "the plan's milestones", wave 4's per-goal read step. Behind
 * `newUi("plan-milestones")` (which itself requires `newUi("plan")`, D166
 * M12), a per-goal step reached by tapping a goal card on `/development`
 * (D166 M1) — `pages/DevelopmentPlan.jsx` owns the gate (`?milestone=<id>`)
 * and passes this component the goal it resolved, or `loading`/`failed`
 * while that resolution is still in flight. Matches `PlanMilestones.dc.html`
 * (round 5, sha256 9fe619fb…) — the approved drawing is the contract, ruled
 * against the issue text by `brief-764.md` and D166
 * (`decisions-2026-09-24.md`) where they differ.
 *
 * REPLACES THE WHOLE PAGE, same mechanism `PlanScreen.jsx`/
 * `RecordEmptyScreen.jsx` use: `useChromelessOverride` + `useRetireTextSize`
 * hide `Layout.jsx`'s shared shell for the mount, and this component
 * supplies its own complete page — its own navy `TopBar`, no shipped
 * `Dialog`, nothing of the old page visible behind it.
 *
 * D166 M2 — a step, not a destination: no `DestinationRow` here (D35 — "a
 * step with a Back link, not a destination"). `milestones-back` returns to
 * today's `/development` goals tab, unaffected — the CALLER clears the
 * `?milestone=` param; this component only fires `onBack`.
 *
 * READ-ONLY, STRUCTURALLY (D166 M5), WITH ONE NAMED EXCEPTION (issue
 * #1021/#255, PR #1017): the per-milestone state control below is the
 * only thing that writes anything, through `PUT /goals/:id/milestones/:i`
 * — and even that never touches the goal's own status, date or percentage
 * (D200.3, D200.4). Assigned-by and "New goal" stay on the list page this
 * step replaces. Update (progress and status) and Delete live here since
 * #1219 (D282), when the page passes `onGoalChanged` / `onGoalDeleted`.
 */
export default function PlanMilestonesScreen({ goal, loading = false, failed = false, onRetry, onBack, onGoalChanged, onGoalDeleted }) {
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();

  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const milestones = Array.isArray(goal?.milestones) ? goal.milestones : [];
  // D166 M6 — a goal with no milestones hides the Milestones group AND the
  // gold below it; every other section (focus, the rest of the plan,
  // sharing) still renders.
  const hasMilestones = milestones.length > 0;
  // F5 — blank reads "Not set" via `FactValue` below, never a fallback
  // label for data the goal doesn't carry. A recognised, non-blank value
  // maps to its drawn label; an unrecognised-but-present one falls back to
  // the raw value itself (still true, never invented) rather than "Not
  // set", which would claim the field is blank when it is not.
  const categoryLabel = goal?.category ? GOAL_CATEGORY_LABELS[goal.category] || goal.category : null;
  const horizonLabel = goal?.horizon ? HORIZON_LABELS_DRAWN[goal.horizon] || goal.horizon : null;
  // Status always has a real value once a goal exists (`normalizeGoalStatus`
  // defaults a falsy one to "not_started"), so no blank case applies here.
  const statusLabel = STATUS_LABELS_DRAWN[normalizeGoalStatus(goal?.status)];

  const goalId = goal?.goal_id;
  const initialStates = () =>
    Array.isArray(goal?.milestone_states) && goal.milestone_states.length === milestones.length
      ? goal.milestone_states
      : milestones.map(() => "not_started");
  const [states, setStates] = useState(initialStates);
  // `{ index, state }` while a ROW's own tap is saving; `null` otherwise.
  // Round 2 review F1 — ANY save in flight, this or the gold's, makes
  // EVERY row AND the gold inert (the artboard's "saving" state: "one
  // save at a time... every segment on the page is inert"); see
  // `anySaving` below, which both the render and every handler check.
  const [saving, setSaving] = useState(null);
  const [goldSaving, setGoldSaving] = useState(false);
  const [errorIndex, setErrorIndex] = useState(null);
  const [lastTap, setLastTap] = useState(null); // { index, state } — what a row's Try again repeats.
  const [goldError, setGoldError] = useState(false); // round 2 review F3.
  const anySaving = saving !== null || goldSaving;

  // Switching to a DIFFERENT goal (a new `?milestone=` id) — a full reset,
  // including any error a PREVIOUS goal left on screen.
  useEffect(() => {
    setStates(initialStates());
    setSaving(null);
    setGoldSaving(false);
    setGoldError(false);
    setErrorIndex(null);
    setLastTap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId]);

  // Round 2 review F7 — a reload of the SAME goal (via `onRetry`, called
  // below on a `milestone_conflict`) resyncs `states` to the fresh server
  // truth, but must NOT clear a visible error: the player has not acted
  // on it yet, and a reload that silently erased "couldn't save that"
  // would read as though the problem had gone away on its own. Separate
  // from the goal-switch effect above on purpose — that one DOES clear
  // errors (a different goal's error means nothing here), this one never
  // does. `initialStates`/`goal` are read fresh from the render this
  // effect runs after, same as the effect above.
  const statesSignature = JSON.stringify(goal?.milestone_states ?? null);
  useEffect(() => {
    if (!goal) return;
    setStates(initialStates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statesSignature]);

  const live = liveIndex(states);
  const showGold = hasMilestones && states.every((s) => s === "not_started");

  function rowNote(i) {
    if (states[i] !== "not_started") return null; // going/done rows carry no second line.
    if (live === i) return i === 0 ? FIRST_UP : NEXT_UP;
    if (live !== -1 && i > live) return WAITS;
    return null;
  }

  async function saveRow(index, state) {
    setSaving({ index, state });
    setErrorIndex(null);
    setGoldError(false); // a row save supersedes any stale gold error.
    try {
      const res = await api.put(`/goals/${goal.goal_id}/milestones/${index}`, { state });
      const data = res.data || {};
      const newStates = Array.isArray(data.milestone_states) ? data.milestone_states : states;
      setStates(newStates);
      setSaving(null);
      setErrorIndex(null);
      setLastTap(null);
    } catch (err) {
      setSaving(null);
      setErrorIndex(index);
      setLastTap({ index, state });
      // D193.2/"a write that races another one loses rather than
      // silently overwriting it" (api/src/routes/goals.ts) — a
      // `milestone_conflict` means the copy this screen is holding is
      // stale, so the fix is the same one every other stale-read path in
      // this app uses: reload through the caller's own `onRetry`, which
      // (via the effect above) resyncs `states` once the fresh goal
      // lands, WITHOUT clearing the message this catch just set (F7).
      // `milestone_out_of_order` and any other failure get the same
      // on-page message (D57) and Try again, with no reload — the
      // artboard draws exactly one "save-failed" state, not three.
      if (err?.response?.status === 409 && err?.response?.data?.code === "milestone_conflict") {
        onRetry?.();
      }
    }
  }

  function tapRow(index, state) {
    if (anySaving) return; // round 2 review F1 — one save at a time, page-wide, either kind.
    if (!isRowInteractive(states, index)) return;
    if (states[index] === state) return; // round 2 review F8 — the already-selected word saves nothing.
    saveRow(index, state);
  }

  function retry() {
    if (!lastTap) return;
    // Round 3 review N3 — the reload a `milestone_conflict` triggers (F7
    // keeps the message up through it) can leave the failed row no
    // longer interactive by the time the player presses Try again
    // (another client finished the order past it, or undid the one
    // undo-eligible row from under it): resending the same tap would
    // just repeat a 409 forever. Re-checked against the CURRENT
    // `states`, not the stale snapshot the error was raised against —
    // if it's no longer interactive, the honest move is to drop the
    // stale error, not to send a PUT that is certain to fail.
    if (!isRowInteractive(states, lastTap.index)) {
      setErrorIndex(null);
      setLastTap(null);
      return;
    }
    saveRow(lastTap.index, lastTap.state);
  }

  // D200.2 — "Start the first one" now actually saves: it sets the first
  // milestone `going` and, on success, unmounts itself (`showGold` above
  // goes false the moment `states[0]` is no longer `not_started`). Round 2
  // review F3 — a failed start is no longer silent: it gets the SAME
  // drawn "save-failed" treatment a row's own failure gets (D57), shown
  // under the gold, with Try again repeating the same attempt.
  async function startFirst() {
    if (anySaving) return; // round 2 review F1.
    setGoldSaving(true);
    setGoldError(false);
    setErrorIndex(null);
    setLastTap(null); // a gold attempt supersedes any stale row error.
    try {
      const res = await api.put(`/goals/${goal.goal_id}/milestones/0`, { state: "going" });
      const data = res.data || {};
      const newStates = Array.isArray(data.milestone_states) ? data.milestone_states : states;
      setStates(newStates);
    } catch {
      setGoldError(true);
    } finally {
      setGoldSaving(false);
    }
  }

  return (
    <div data-testid="milestones" style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="navy"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact />
            <AccessibilityMenu hideTextSize />
            <AccountMenu empty />
          </>
        }
        data-testid="milestones-topbar"
      />

      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "22px 20px 20px" }}>
        <SecondaryAction
          variant="link"
          onClick={onBack}
          data-testid="milestones-back"
          style={{
            justifyContent: "flex-start",
            alignSelf: "flex-start",
            fontFamily: "inherit",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--nw-ink-muted)",
            textDecoration: "underline",
          }}
        >
          Back
        </SecondaryAction>

        {loading && (
          <div
            data-testid="milestones-loading"
            data-dc-state="loading"
            className="flex flex-col"
            style={{ gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px dashed var(--nw-line-strong)", borderRadius: "var(--nw-radius-card)" }}
          >
            {/* Issue #764 review round 1, F6 — no sentence while loading,
                the same rule `RecordEmptyScreen.jsx`'s own `loading` state
                follows: skeleton shapes only. The drawn "While the plan
                loads" line sits inside the artboard's own `data-dc-note`
                container (a reviewer-facing label on the commentary block,
                never real copy) and is not built. */}
            <div className="flex flex-col" style={{ gap: 10 }}>
              <SkeletonBar width="35%" />
              <SkeletonBar width="80%" />
              <SkeletonBar width="60%" />
            </div>
            <div className="flex flex-col" style={{ gap: 12, padding: 16, background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex justify-between" style={{ gap: 12 }}>
                  <SkeletonBar width="55%" />
                  <SkeletonBar width="22%" />
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && failed && (
          <LoadFailureNote what="your development plan" testId="goals-error-short" onRetry={onRetry} />
        )}

        {!loading && !failed && goal && (
          <>
            <div className="flex flex-col" style={{ gap: 8 }}>
              <div className="nw-eyebrow">This season's focus</div>
              <div
                data-testid="milestones-focus"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 800,
                  fontSize: 24,
                  letterSpacing: "-0.5px",
                  lineHeight: 1.22,
                  color: "var(--nw-ink)",
                }}
              >
                “{goal.target || goal.goal_title}”
              </div>
              <div className="nw-prose" style={{ color: "var(--nw-ink-muted)" }}>
                Your sentence, unchanged. Everything under it is yours too — these are the lines you wrote, kept in
                your order.
              </div>
            </div>

            {hasMilestones && (
              <div className="flex flex-col" style={{ gap: 10 }}>
                <div className="nw-section">Milestones</div>
                {milestones.map((text, i) => {
                  const interactive = isRowInteractive(states, i) && !anySaving;
                  const disabled = !interactive;
                  const savingSegment = saving && saving.index === i ? saving.state : null;
                  const note = rowNote(i);
                  const showError = errorIndex === i && lastTap && lastTap.index === i;
                  const titleId = `milestone-title-${i}`;
                  return (
                    <div
                      key={i}
                      data-testid={i === 0 ? "milestones-list" : `milestone-row-${i}`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        padding: 14,
                        background: "var(--nw-surface)",
                        border: "1px solid var(--nw-line)",
                        borderRadius: "var(--nw-radius-card)",
                      }}
                    >
                      <MilestoneGlyph state={states[i]} live={i === live} />
                      <div className="flex flex-col" style={{ gap: 4, flexGrow: 1, minWidth: 0 }}>
                        <div id={titleId} className="nw-body-strong" style={{ lineHeight: 1.4 }}>
                          {text}
                        </div>
                        {note && <div className="nw-help" style={{ lineHeight: "normal" }}>{note}</div>}
                        <MilestoneSegments
                          index={i}
                          state={states[i]}
                          disabled={disabled}
                          savingSegment={savingSegment}
                          onTap={(state) => tapRow(i, state)}
                          titleId={titleId}
                        />
                        {showError && <SaveErrorBlock state={states[i]} onRetry={retry} />}
                      </div>
                    </div>
                  );
                })}
                <div className="nw-help">
                  No percentage, no bar at nought. A milestone is not started, going, or done — and it says which.
                </div>
              </div>
            )}

            <div
              className="flex flex-col"
              style={{ gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
            >
              <div data-testid="milestones-plan-facts" className="nw-section">The rest of the plan</div>
              <FactRow label="Horizon">
                <FactValue value={horizonLabel} />
              </FactRow>
              <FactRow label="Category">
                <FactValue value={categoryLabel} />
              </FactRow>
              <FactRow label="Due">
                <FactValue value={goal.target_date} />
              </FactRow>
              <FactRow label="Status">
                <FactValue value={statusLabel} />
              </FactRow>
              <div className="flex flex-col" style={{ gap: 4 }}>
                <span style={{ fontSize: 13, color: "var(--nw-ink-muted)" }}>Notes</span>
                {goal.notes ? (
                  <span style={{ fontSize: 14, lineHeight: 1.45, color: "var(--nw-ink)" }}>“{goal.notes}”</span>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-ink-faint)" }}>Not set</span>
                )}
              </div>
            </div>

            {(onGoalChanged || onGoalDeleted) && (
              <PlanGoalManage goal={goal} onChanged={onGoalChanged} onDeleted={onGoalDeleted} />
            )}

            <div
              className="flex items-start"
              style={{ gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0B2E59"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ marginTop: 1, flexShrink: 0 }}
              >
                <path d="M17 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9.5" cy="7" r="3.5" />
                <path d="M22 20v-2a4 4 0 0 0-3-3.9" />
              </svg>
              <span data-testid="milestones-sharing" style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
                Every coach you're linked to can read this, in these words — that's how sharing works here, by the
                link and not by the person.
              </span>
            </div>

            <div style={{ flexGrow: 1 }} />

            {hasMilestones && (
              <div className="flex flex-col" style={{ gap: 10 }}>
                {showGold && (
                  <>
                    <ActionButton
                      data-testid="milestones-start-first"
                      onClick={startFirst}
                      disabled={anySaving}
                      icon={goldSaving ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : undefined}
                    >
                      {goldSaving ? STARTING : "Start the first one"}
                    </ActionButton>
                    {goldError && <SaveErrorBlock state="not_started" onRetry={startFirst} />}
                  </>
                )}
                {live !== -1 && (
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)", textAlign: "center" }}>
                    One at a time. The next waits until this one's done.
                  </div>
                )}
                {live === -1 && (
                  <div
                    data-testid="milestones-all-done"
                    className="flex flex-col"
                    style={{ gap: 6, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
                  >
                    <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 17, color: "var(--nw-ink)" }}>
                      {ALL_DONE(milestones.length)}
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>{ALL_DONE_NOTE}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
